import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';

interface Resource {
  id: number;
  title: string;
  description: string | null;
  filename: string;
  contentType: string | null;
  size: number | null;
  audience: 'all' | 'accepted';
}

const AUDIENCE_LABEL: Record<string, string> = {
  all: 'All applicants',
  accepted: 'Accepted only',
};

export default function AdminResources() {
  const navigate = useNavigate();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Upload form state
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState<'all' | 'accepted'>('all');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadErr, setUploadErr] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const load = () => {
    API.adminResources()
      .then(data => setResources(data?.resources ?? []))
      .catch(() => setError('Unable to load resources.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pickFile = (f: File | null) => {
    if (!f) return;
    setFile(f);
    setUploadErr('');
    // Default the title to the filename (sans extension) if the admin hasn't typed one.
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setUploadErr('');
    try {
      const res = await API.resourceUploadUrl(file.name, file.type || 'application/octet-stream');
      if (!res?.uploadUrl) throw new Error('no-url');
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(res.method || 'PUT', res.uploadUrl);
        Object.entries(res.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, String(v)));
        xhr.upload.onprogress = e => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`s3-${xhr.status}`));
        xhr.onerror = () => reject(new Error('network'));
        xhr.send(file);
      });
      await API.createResource({
        ...res.descriptor,
        title: title.trim() || file.name,
        description: description.trim() || null,
        audience,
        size: file.size,
      });
      // Reset the form and refresh the list.
      setFile(null);
      setTitle('');
      setDescription('');
      setAudience('all');
      if (inputRef.current) inputRef.current.value = '';
      load();
    } catch (e: any) {
      const msg = String(e?.message || '');
      setUploadErr(msg.includes('503')
        ? 'File uploads aren’t enabled in this environment yet.'
        : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;

  return (
    <>
      <div className="phil-page-heading">Applicant Resources</div>
      <div className="phil-page-sub">
        Upload decks, guides, and other materials for the Resources section applicants see.
        Choose whether each item is visible to all applicants or only those accepted into the Lab.
      </div>
      <div style={{ marginBottom: 20 }}>
        <span className="phil-section-sub" onClick={() => navigate('/admin/settings')}>← Back to Settings</span>
      </div>

      {/* Upload card */}
      <div className="phil-card" style={{ marginBottom: 24 }}>
        <div className="phil-card-head">
          <h2>Add a Resource</h2>
          <span style={{ fontSize: 12, color: '#888' }}>PPTX, PDF, DOCX, XLSX, images…</span>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div className="phil-form-field" style={{ marginBottom: 16 }}>
            <div className="phil-q-label">File</div>
            {file ? (
              <div className="phil-file-chip">
                <span className="phil-file-name">📎 {file.name}</span>
                <span style={{ fontSize: 12, color: '#888' }}>{formatSize(file.size)}</span>
                {!uploading && (
                  <button type="button" className="phil-file-remove" onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ''; }}>
                    Remove
                  </button>
                )}
              </div>
            ) : (
              <div
                className={`phil-upload phil-upload-interactive${dragOver ? ' dragover' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0] || null); }}
              >
                📎 Click to upload or drag &amp; drop
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={e => pickFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="phil-form-field" style={{ marginBottom: 16 }}>
            <div className="phil-q-label">Title</div>
            <input
              className="phil-input"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. FinTech Innovation Lab — Program Overview"
            />
          </div>

          <div className="phil-form-field" style={{ marginBottom: 16 }}>
            <div className="phil-q-label">Description <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span></div>
            <textarea
              className="phil-textarea"
              style={{ height: 64 }}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="A short note shown under the title."
            />
          </div>

          <div className="phil-form-field" style={{ marginBottom: 20 }}>
            <div className="phil-q-label">Visible to</div>
            <select
              className="phil-input"
              style={{ width: 260, padding: '6px 10px' }}
              value={audience}
              onChange={e => setAudience(e.target.value as 'all' | 'accepted')}
            >
              <option value="all">All applicants</option>
              <option value="accepted">Accepted applicants only</option>
            </select>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
              "Accepted only" resources appear once an applicant is selected into the Lab.
            </div>
          </div>

          {uploadErr && <div className="phil-error" style={{ marginBottom: 12 }}>{uploadErr}</div>}

          <button
            className="phil-btn phil-btn-primary"
            onClick={upload}
            disabled={!file || uploading}
          >
            {uploading ? `Uploading… ${progress}%` : 'Upload Resource'}
          </button>
        </div>
      </div>

      {/* Existing resources */}
      <div className="phil-card">
        <div className="phil-card-head">
          <h2>Current Resources</h2>
          <span style={{ fontSize: 12, color: '#888' }}>{resources.length} item{resources.length === 1 ? '' : 's'}</span>
        </div>
        {error && <div className="phil-error" style={{ margin: '16px 24px' }}>{error}</div>}
        {resources.length === 0 ? (
          <div style={{ padding: '32px 24px', textAlign: 'center', color: '#888', fontSize: 13 }}>
            No resources uploaded yet.
          </div>
        ) : (
          <table className="phil-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>File</th>
                <th>Visible to</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resources.map(r => (
                <ResourceRow key={r.id} resource={r} onChanged={load} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function ResourceRow({ resource, onChanged }: { resource: Resource; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(resource.title);
  const [description, setDescription] = useState(resource.description ?? '');
  const [audience, setAudience] = useState<'all' | 'accepted'>(resource.audience);
  const [busy, setBusy] = useState(false);

  const view = async () => {
    try {
      const res = await API.resourceFileUrl(resource.id);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch { /* ignore */ }
  };

  const save = async () => {
    setBusy(true);
    try {
      await API.updateResource(resource.id, { title: title.trim(), description: description.trim(), audience });
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${resource.title}"? Applicants will no longer see it.`)) return;
    setBusy(true);
    try {
      await API.deleteResource(resource.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={4} style={{ background: '#f9fbfd' }}>
          <div style={{ padding: '12px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input className="phil-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" />
            <textarea className="phil-textarea" style={{ height: 56 }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" />
            <select className="phil-input" style={{ width: 260, padding: '6px 10px' }} value={audience} onChange={e => setAudience(e.target.value as 'all' | 'accepted')}>
              <option value="all">All applicants</option>
              <option value="accepted">Accepted applicants only</option>
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="phil-btn phil-btn-primary phil-btn-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={() => { setEditing(false); setTitle(resource.title); setDescription(resource.description ?? ''); setAudience(resource.audience); }} disabled={busy}>Cancel</button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{resource.title}</div>
        {resource.description && <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{resource.description}</div>}
      </td>
      <td style={{ color: '#555' }}>
        <span>{resource.filename}</span>
        {resource.size != null && <span style={{ color: '#aaa', marginLeft: 6 }}>({formatSize(resource.size)})</span>}
      </td>
      <td>
        <span className={`phil-badge ${resource.audience === 'accepted' ? 'phil-badge-review' : 'phil-badge-submitted'}`}>
          {AUDIENCE_LABEL[resource.audience]}
        </span>
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button className="phil-file-link" onClick={view}>View</button>
        <button className="phil-file-link" style={{ marginLeft: 12 }} onClick={() => setEditing(true)}>Edit</button>
        <button className="phil-file-remove" style={{ marginLeft: 12 }} onClick={remove} disabled={busy}>Delete</button>
      </td>
    </tr>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
